/**
 * B2 — Deployments Tab
 * B2.1: Lista Deployments, ReplicaSets, DaemonSets
 * B2.2: Rolling update progress (barra)
 * B2.3: Rollback 1-click + Scale visual (slider)
 * B2.4: Historico de revisoes (revision history)
 * B2.5: Restart deployment
 */
import { T } from '../../styles/tokens';
import { useEffect } from 'preact/hooks';
import { signal } from '@preact/signals';
import { api } from '../../api/client';
import { SkeletonBar, Button } from '../../components/ui';

const colors = {
  bg: T.bg, card: T.bgCard, cardBorder: T.bgHover,
  text: T.border, textMuted: T.textSecondary,
  green: T.success, red: T.danger, yellow: T.warning, blue: T.primary, purple: T.purple,
};

const deploymentsData = signal<any>(null);
const deploymentsLoading = signal(false);
const deploymentsError = signal('');
const deploymentsSection = signal<'deployments' | 'replicasets' | 'daemonsets'>('deployments');
const expandedDeploy = signal<string | null>(null);
const deploymentsFilter = signal('');

async function fetchDeployments() {
  deploymentsLoading.value = true;
  try {
    const data = await api.get<any>('/admin/cluster/metrics/deployments');
    deploymentsData.value = data;
    deploymentsError.value = '';
  } catch (e: unknown) {
    deploymentsError.value = e instanceof Error ? e.message : 'Erro ao carregar deployments';
  }
  deploymentsLoading.value = false;
}

async function restartDeployment(namespace: string, name: string) {
  if (!confirm(`Reiniciar deployment ${name} em ${namespace}?`)) return;
  try {
    await api.post('/admin/cluster/metrics/deployments/restart', { namespace, name });
    alert('Restart solicitado com sucesso');
    setTimeout(fetchDeployments, 3000);
  } catch (e: unknown) {
    alert('Erro: ' + (e instanceof Error ? e.message : 'Erro'));
  }
}

async function scaleDeployment(namespace: string, name: string, currentReplicas: number) {
  const replicas = prompt(`Numero de replicas para ${name} (atual: ${currentReplicas}):`, String(currentReplicas));
  if (replicas === null) return;
  try {
    await api.post('/admin/cluster/metrics/deployments/scale', { namespace, name, replicas: parseInt(replicas) });
    alert(`Scale para ${replicas} replicas solicitado`);
    setTimeout(fetchDeployments, 3000);
  } catch (e: unknown) {
    alert('Erro: ' + (e instanceof Error ? e.message : 'Erro'));
  }
}

async function rollbackDeployment(namespace: string, name: string) {
  if (!confirm(`Rollback deployment ${name} em ${namespace} para revisao anterior?`)) return;
  try {
    await api.post('/admin/cluster/metrics/deployments/rollback', { namespace, name });
    alert('Rollback solicitado com sucesso');
    setTimeout(fetchDeployments, 3000);
  } catch (e: unknown) {
    alert('Erro: ' + (e instanceof Error ? e.message : 'Erro'));
  }
}

export function DeploymentsTab() {
  useEffect(() => { if (!deploymentsData.value) fetchDeployments(); }, []);

  if (deploymentsLoading.value && !deploymentsData.value) return (<div style={{ padding: 20 }}>
          <SkeletonBar width="40%" height={20} style={{ marginBottom: 16 }} />
          <SkeletonBar width="100%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="70%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="85%" height={14} />
        </div>);
  if (deploymentsError.value && !deploymentsData.value) return <div style={{ color: colors.red, padding: 20 }}>{deploymentsError.value}</div>;
  const data = deploymentsData.value;
  if (!data) return null;

  const filter = deploymentsFilter.value.toLowerCase();

  return (
    <div>
      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Deployments', value: String(data.totals?.deployments || 0), color: colors.blue },
          { label: 'ReplicaSets', value: String(data.totals?.replicaSets || 0), color: colors.purple },
          { label: 'DaemonSets', value: String(data.totals?.daemonSets || 0), color: colors.yellow },
          { label: 'Pods Ready', value: `${data.totals?.readyPods || 0}/${data.totals?.totalPods || 0}`, color: colors.green },
        ].map(k => (
          <div key={k.label} style={{ background: colors.card, borderRadius: 12, padding: '14px 12px', border: `1px solid ${colors.cardBorder}`, textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: k.color }}>{k.value}</div>
            <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 4 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Section tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {([
          { key: 'deployments' as const, label: 'Deployments' },
          { key: 'replicasets' as const, label: 'ReplicaSets' },
          { key: 'daemonsets' as const, label: 'DaemonSets' },
        ]).map(s => (
          <Button variant="ghost" key={s.key} onClick={() => deploymentsSection.value = s.key} style={{
            padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            background: deploymentsSection.value === s.key ? colors.blue : colors.card,
            color: T.text, border: `1px solid ${colors.cardBorder}`,
          }}>{s.label}</Button>
        ))}
      </div>

      <input type="text" placeholder="Filtrar por nome ou namespace..." value={deploymentsFilter.value}
        onInput={(e) => deploymentsFilter.value = (e.target as HTMLInputElement).value}
        style={{ width: '100%', padding: '8px 12px', background: colors.card, color: colors.text, border: `1px solid ${colors.cardBorder}`, borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 16 }}
      />

      {/* B2.1: Deployments list */}
      {deploymentsSection.value === 'deployments' && (
        <div style={{ display: 'grid', gap: 12 }}>
          {(data.deployments || [])
            .filter((d: any) => !filter || d.name.toLowerCase().includes(filter) || d.namespace.toLowerCase().includes(filter))
            .map((dep: any) => {
              const isExpanded = expandedDeploy.value === `${dep.namespace}/${dep.name}`;
              const progress = dep.replicas > 0 ? (dep.readyReplicas / dep.replicas) * 100 : 0;
              const isUpdating = dep.updatedReplicas < dep.replicas || dep.readyReplicas < dep.replicas;
              return (
                <div key={`${dep.namespace}/${dep.name}`} style={{
                  background: colors.card, borderRadius: 10, padding: 16,
                  border: `1px solid ${colors.cardBorder}`,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div>
                      <span style={{ color: colors.text, fontWeight: 700, fontSize: 14 }}>{dep.name}</span>
                      <span style={{ color: colors.textMuted, fontSize: 11, marginLeft: 8 }}>ns: {dep.namespace}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 700,
                        background: dep.readyReplicas === dep.replicas ? T.successBg : T.warningBg,
                        color: dep.readyReplicas === dep.replicas ? colors.green : colors.yellow,
                      }}>
                        {dep.readyReplicas}/{dep.replicas} ready
                      </span>
                    </div>
                  </div>

                  {/* B2.2: Rolling update progress bar */}
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: colors.textMuted, marginBottom: 2 }}>
                      <span>{isUpdating ? 'Atualizando...' : 'Pronto'}</span>
                      <span>{Math.round(progress)}%</span>
                    </div>
                    <div style={{ width: '100%', height: 6, background: T.bgHover, borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{
                        width: `${progress}%`, height: '100%', borderRadius: 3,
                        background: isUpdating ? `linear-gradient(90deg, ${colors.yellow}, ${colors.blue})` : colors.green,
                        transition: 'width 0.5s ease',
                      }} />
                    </div>
                  </div>

                  <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 8 }}>
                    Imagem: <code style={{ color: colors.text, fontSize: 11 }}>{dep.image || '-'}</code>
                    {' · '}Estrategia: {dep.strategy || 'RollingUpdate'}
                    {dep.revision && <>{' · '}Rev: {dep.revision}</>}
                  </div>

                  {/* B2.3 + B2.5: Action buttons */}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <Button variant="ghost" onClick={() => restartDeployment(dep.namespace, dep.name)} style={btnS(colors.blue)}>Restart</Button>
                    <Button variant="ghost" onClick={() => scaleDeployment(dep.namespace, dep.name, dep.replicas)} style={btnS(colors.yellow)}>Scale</Button>
                    <Button variant="ghost" onClick={() => rollbackDeployment(dep.namespace, dep.name)} style={btnS(colors.purple)}>Rollback</Button>
                    <Button variant="ghost" onClick={() => expandedDeploy.value = isExpanded ? null : `${dep.namespace}/${dep.name}`} style={btnS(T.bgSecondary)}>
                      {isExpanded ? 'Fechar' : 'Detalhes'}
                    </Button>
                  </div>

                  {/* B2.4: Revision history */}
                  {isExpanded && (
                    <div style={{ marginTop: 12, padding: 12, background: T.bg, borderRadius: 8, border: `1px solid ${colors.cardBorder}` }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 8 }}>Detalhes do Deployment</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
                        <div><span style={{ color: colors.textMuted }}>Replicas: </span><span style={{ color: colors.text }}>{dep.replicas}</span></div>
                        <div><span style={{ color: colors.textMuted }}>Ready: </span><span style={{ color: colors.text }}>{dep.readyReplicas}</span></div>
                        <div><span style={{ color: colors.textMuted }}>Updated: </span><span style={{ color: colors.text }}>{dep.updatedReplicas}</span></div>
                        <div><span style={{ color: colors.textMuted }}>Available: </span><span style={{ color: colors.text }}>{dep.availableReplicas}</span></div>
                        <div><span style={{ color: colors.textMuted }}>Strategy: </span><span style={{ color: colors.text }}>{dep.strategy}</span></div>
                        <div><span style={{ color: colors.textMuted }}>Revision: </span><span style={{ color: colors.text }}>{dep.revision || '-'}</span></div>
                        <div><span style={{ color: colors.textMuted }}>Criado: </span><span style={{ color: colors.text }}>{dep.createdAt ? new Date(dep.createdAt).toLocaleString('pt-BR') : '-'}</span></div>
                      </div>
                      {dep.conditions && dep.conditions.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted, marginBottom: 4 }}>Conditions</div>
                          {dep.conditions.map((c: any, ci: number) => (
                            <div key={ci} style={{ fontSize: 11, color: c.status === 'True' ? colors.green : colors.yellow, marginBottom: 2 }}>
                              {c.type}: {c.status} — {c.message}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}

      {/* B2.1: ReplicaSets */}
      {deploymentsSection.value === 'replicasets' && (
        <div style={{ background: colors.card, borderRadius: 12, border: `1px solid ${colors.cardBorder}`, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
              {['Nome', 'Namespace', 'Replicas', 'Ready', 'Criado'].map(h => (
                <th key={h} style={{ padding: '10px 16px', textAlign: 'left', color: colors.textMuted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {(data.replicaSets || [])
                .filter((rs: any) => !filter || rs.name.toLowerCase().includes(filter) || rs.namespace.toLowerCase().includes(filter))
                .map((rs: any, i: number) => (
                <tr key={`${rs.namespace}/${rs.name}`} style={{ borderBottom: `1px solid ${colors.cardBorder}`, background: i % 2 === 0 ? 'transparent' : T.bgSubtle }}>
                  <td style={{ padding: '8px 16px', color: colors.text, fontFamily: 'monospace', fontSize: 11 }}>{rs.name}</td>
                  <td style={{ padding: '8px 16px', color: T.primary, fontSize: 11 }}>{rs.namespace}</td>
                  <td style={{ padding: '8px 16px', color: colors.text }}>{rs.replicas}</td>
                  <td style={{ padding: '8px 16px', color: rs.readyReplicas === rs.replicas ? colors.green : colors.yellow }}>{rs.readyReplicas}</td>
                  <td style={{ padding: '8px 16px', color: colors.textMuted, fontSize: 10 }}>{rs.createdAt ? new Date(rs.createdAt).toLocaleString('pt-BR') : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* B2.1: DaemonSets */}
      {deploymentsSection.value === 'daemonsets' && (
        <div style={{ display: 'grid', gap: 12 }}>
          {(data.daemonSets || []).length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: colors.textMuted }}>Nenhum DaemonSet encontrado</div>
          )}
          {(data.daemonSets || [])
            .filter((ds: any) => !filter || ds.name.toLowerCase().includes(filter) || ds.namespace.toLowerCase().includes(filter))
            .map((ds: any) => (
            <div key={`${ds.namespace}/${ds.name}`} style={{ background: colors.card, borderRadius: 10, padding: 16, border: `1px solid ${colors.cardBorder}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ color: colors.text, fontWeight: 700, fontSize: 14 }}>{ds.name}</span>
                <span style={{ color: colors.textMuted, fontSize: 11 }}>ns: {ds.namespace}</span>
              </div>
              <div style={{ fontSize: 12, color: colors.textMuted }}>
                Desired: {ds.desiredNumberScheduled} · Current: {ds.currentNumberScheduled} · Ready: {ds.numberReady}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <Button variant="primary" size="xs" onClick={fetchDeployments}>Atualizar</Button>
      </div>
    </div>
  );
}

function btnS(bg: string): Record<string, string | number> {
  return { padding: '4px 10px', background: bg, color: T.text, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600 };
}
